import { Fragment } from 'react'
import { fromBidType, LEVELS, VARIANTS, SOLO } from '../../lib/scoring'
import type { Level, Variant, Solo } from '../../lib/scoring'
import type { BidType } from '../../../../shared/types'

export interface BidHeatmapItem {
  bidType: BidType
  count: number
}

interface BidHeatmapProps {
  items: BidHeatmapItem[]
  ariaLabel: string
  emptyMark?: string
}

const VARIANT_LABELS: Record<Variant, string> = {
  Almindelige: 'Alm.',
  Halve: 'Halve',
  Gode: 'Gode',
  '1. Vip': '1. Vip',
  '2. Vip': '2. Vip',
  '3. Vip': '3. Vip'
}

const SOLO_LABELS: Record<Solo, string> = {
  Sol: 'Sol',
  'Ren sol': 'Ren sol',
  'Ren bordlægger': 'Ren bordl.',
  'Dobbelt bordlægger': 'Dobbelt bordl.'
}

// Fulde klasse-strenge (ikke sammensat af variabler), så Tailwind kan finde dem ved scanning.
const MINT_STEPS = ['bg-mint/10', 'bg-mint/18', 'bg-mint/30', 'bg-mint/45']

function stepIndex(ratio: number): number {
  if (ratio >= 0.25) return 3
  if (ratio >= 0.12) return 2
  if (ratio >= 0.04) return 1
  return 0
}

function cellBgClass(count: number, total: number): string {
  if (count === 0) return 'bg-text-2/10'
  const ratio = total > 0 ? count / total : 0
  return MINT_STEPS[stepIndex(ratio)]
}

function Cell({ count, total, title, emptyMark }: { count: number; total: number; title: string; emptyMark: string }) {
  return (
    <span
      title={title}
      className={`rounded-md py-1.5 font-mono ${count === 0 ? 'text-text-2/40' : 'font-semibold'} ${cellBgClass(count, total)}`}
    >
      {count === 0 ? emptyMark : count}
    </span>
  )
}

/** Heatmap af meldingsantal: niveau × variant, plus en separat solo-række. Farve viser andel af alle meldinger. */
function BidHeatmap({ items, ariaLabel, emptyMark = '·' }: BidHeatmapProps) {
  const grid = new Map<Level, Map<Variant, number>>(LEVELS.map((level) => [level, new Map<Variant, number>()]))
  const soloCounts = new Map<Solo, number>()
  let total = 0

  for (const item of items) {
    total += item.count
    const parsed = fromBidType(item.bidType)
    if (parsed.solo) {
      soloCounts.set(parsed.solo, (soloCounts.get(parsed.solo) ?? 0) + item.count)
    } else if (parsed.level && parsed.variant) {
      const row = grid.get(parsed.level)
      if (row) row.set(parsed.variant, (row.get(parsed.variant) ?? 0) + item.count)
    }
  }

  return (
    <div role="img" aria-label={ariaLabel}>
      <div
        className="grid gap-1 overflow-x-auto text-center text-[0.7rem]"
        style={{ gridTemplateColumns: `36px repeat(${VARIANTS.length}, minmax(38px, 1fr))` }}
      >
        <span />
        {VARIANTS.map((v) => (
          <span key={v} className="truncate px-0.5 py-1 text-text-2">
            {VARIANT_LABELS[v]}
          </span>
        ))}
        {LEVELS.map((level) => (
          <Fragment key={level}>
            <span className="py-1 text-left text-text-2">{level}</span>
            {VARIANTS.map((v) => {
              const count = grid.get(level)?.get(v) ?? 0
              return (
                <Cell
                  key={v}
                  count={count}
                  total={total}
                  title={`${level} ${v}: ${count}`}
                  emptyMark={emptyMark}
                />
              )
            })}
          </Fragment>
        ))}
      </div>

      <div
        className="mt-2 grid gap-1 overflow-x-auto text-center text-[0.7rem]"
        style={{ gridTemplateColumns: `36px repeat(${SOLO.length}, minmax(38px, 1fr))` }}
      >
        <span />
        {SOLO.map((s) => (
          <span key={s} className="truncate px-0.5 py-1 text-text-2">
            {SOLO_LABELS[s]}
          </span>
        ))}
        <span className="py-1 text-left text-text-2">Solo</span>
        {SOLO.map((s) => {
          const count = soloCounts.get(s) ?? 0
          return <Cell key={s} count={count} total={total} title={`${SOLO_LABELS[s]}: ${count}`} emptyMark={emptyMark} />
        })}
      </div>
    </div>
  )
}

export default BidHeatmap
